import { useEffect, useState } from 'react';

import { PLANNERS_TAXONOMY_KEY } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { notLargeDesktopV2 } from '@zola-helpers/client/dist/es/util/responsive';
import { getLocalStorage } from '@zola-helpers/client/dist/es/util/storage';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import { useHistory, useLocation } from 'react-router-dom';

import useCurrentStorefront from '~/components/common/hooks/useCurrentStorefront';
import { useVendorDashboardUpgradeModal } from '~/components/common/modals/VendorDashboardUpgradeModal';
import { useVendorNewExperienceModal } from '~/components/common/modals/VendorNewExperienceModal';
import { useAnyModalShown } from '~/hooks/useAnyModalShown';
import { getVendorUserSettings, setVendorUserSetting } from '~/libs/client/api/vendorUser';
import { isAutomatedTest } from '~/util/automatedTestUtils';

import { StepComponent } from './ShadowboxTour/components/StepContent';
import {
  NAV_STEPS,
  LEADS_STEPS,
  LEADS_STEPS_PLANNERS,
  SPLIT_SCREEN_STEPS,
} from './ShadowboxTour/components/Steps';
import ShadowboxTour from './ShadowboxTour/ShadowboxTour';

const NAV_TOUR_SEEN_KEY = 'marketplace:vendor:nav-tour-seen-april-2024';
const LEADS_TOUR_SEEN_KEY = 'marketplace:vendor:leads-tour-seen-april-2024';
const SPLIT_SCREEN_TOUR_SEEN_KEY = 'marketplace:vendor:split-screen-tour-seen-april-2024';
/**
 * @deprecated Use user settings instead of local storage
 */
const VENDOR_SPLIT_SCREEN_TOUR_SHOWN = 'VENDOR_SPLIT_SCREEN_TOUR_SHOWN';

export const VendorTour = () => {
  const location = useLocation();
  const history = useHistory();
  const anyModalShown = useAnyModalShown();
  const storefrontDetails = useCurrentStorefront();
  const { showVendorDashboardUpgradeModal } = useVendorDashboardUpgradeModal();
  const { showVendorNewExperienceModal } = useVendorNewExperienceModal();
  const [navTourSeen, setNavTourSeen] = useState(true);
  const [leadsTourSeen, setLeadsTourSeen] = useState(true);
  const [splitScreenTourSeen, setSplitScreenTourSeen] = useState(true);
  const [showTour, setShowTour] = useState<string | null>();
  const [showShadowboxTour, setShowShadowboxTour] = useState(false);
  const [steps, setSteps] = useState<StepComponent[]>([]);
  // The type is important here! To store a reference to the callback, the state hook needs a function that returns a function
  const [onTourComplete, setOnTourComplete] = useState<() => () => void>();

  useEffectOnce(() => {
    getVendorUserSettings([NAV_TOUR_SEEN_KEY, LEADS_TOUR_SEEN_KEY, SPLIT_SCREEN_TOUR_SEEN_KEY])
      .then((userSettingsMap) => {
        const { settings = {} } = userSettingsMap;
        setNavTourSeen(Boolean(settings[NAV_TOUR_SEEN_KEY]?.flag));
        setLeadsTourSeen(Boolean(settings[LEADS_TOUR_SEEN_KEY]?.flag));
        // Migrating the split screen tour from local storage to user settings
        setSplitScreenTourSeen(
          Boolean(settings[SPLIT_SCREEN_TOUR_SEEN_KEY]?.flag) ||
            getLocalStorage(VENDOR_SPLIT_SCREEN_TOUR_SHOWN) === 'true'
        );
      })
      .catch(() => {
        setNavTourSeen(true);
        setLeadsTourSeen(true);
        setSplitScreenTourSeen(true);
      });
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.has('showTour')) {
      setShowTour(params.get('showTour'));
      params.delete('showTour');
      history.replace([location.pathname, params.toString()].filter(Boolean).join('?'));
    }
  }, [history, location]);

  useEffect(() => {
    const canShowTour = !isAutomatedTest() && !anyModalShown && storefrontDetails;

    if (!canShowTour) {
      return;
    }

    const { pathname } = window.location;
    const isNavTourPage =
      !pathname.startsWith('/inspire/vendors/leads') &&
      !pathname.startsWith('/inspire/vendors/listing');
    const isLeadsTourPage = pathname.startsWith('/inspire/vendors/leads');
    const isSplitScreenTourPage = pathname.startsWith('/inspire/vendors/listing');
    const isMobileTourPage = !pathname.startsWith('/inspire/vendors/listing');
    const isPublished = Boolean(storefrontDetails?.publishedAt);

    if (notLargeDesktopV2()) {
      if (isMobileTourPage && (!navTourSeen || showTour === 'dashboard')) {
        setNavTourSeen(true);
        setLeadsTourSeen(true);
        setShowTour(null);
        showVendorNewExperienceModal({
          startButtonText: 'Got it!',
          hideSkipButton: true,
          onTourStart: () => {
            setVendorUserSetting(NAV_TOUR_SEEN_KEY).catch(() => null);
            setVendorUserSetting(LEADS_TOUR_SEEN_KEY).catch(() => null);
          },
        });
      }
      return;
    }

    if (isNavTourPage && (!navTourSeen || showTour === 'dashboard')) {
      setNavTourSeen(true);
      setShowTour(null);
      showVendorDashboardUpgradeModal({
        onTourStart: () => {
          setShowShadowboxTour(true);
          setSteps(NAV_STEPS);
          setOnTourComplete(() => () => {
            setShowShadowboxTour(false);
            setVendorUserSetting(NAV_TOUR_SEEN_KEY).catch(() => null);
          });
        },
        onTourSkip: () => {
          setVendorUserSetting(NAV_TOUR_SEEN_KEY).catch(() => null);
        },
      });
      return;
    }

    if (isLeadsTourPage && (!leadsTourSeen || showTour === 'leads')) {
      setLeadsTourSeen(true);
      setShowTour(null);
      const leadsSteps =
        storefrontDetails.taxonomyKey === PLANNERS_TAXONOMY_KEY
          ? LEADS_STEPS_PLANNERS
          : LEADS_STEPS;
      if (!navTourSeen) {
        setNavTourSeen(true);
        showVendorNewExperienceModal({
          startButtonText: 'Check out your new Leads page',
          onTourStart: () => {
            setShowShadowboxTour(true);
            setSteps(leadsSteps);
            setOnTourComplete(() => () => {
              setShowShadowboxTour(false);
              setVendorUserSetting(NAV_TOUR_SEEN_KEY).catch(() => null);
              setVendorUserSetting(LEADS_TOUR_SEEN_KEY).catch(() => null);
            });
          },
          onTourSkip: () => {
            setVendorUserSetting(NAV_TOUR_SEEN_KEY).catch(() => null);
            setVendorUserSetting(LEADS_TOUR_SEEN_KEY).catch(() => null);
          },
        });
      } else {
        setShowShadowboxTour(true);
        setSteps(leadsSteps);
        setOnTourComplete(() => () => {
          setShowShadowboxTour(false);
          setVendorUserSetting(LEADS_TOUR_SEEN_KEY).catch(() => null);
        });
      }
      return;
    }

    if (
      isSplitScreenTourPage &&
      ((!isPublished && !splitScreenTourSeen) || showTour === 'edit-listing')
    ) {
      setSplitScreenTourSeen(true);
      setShowTour(null);
      setShowShadowboxTour(true);
      setSteps(SPLIT_SCREEN_STEPS);
      setOnTourComplete(() => () => {
        setShowShadowboxTour(false);
        setVendorUserSetting(SPLIT_SCREEN_TOUR_SEEN_KEY).catch(() => null);
      });
      return;
    }
  }, [
    anyModalShown,
    leadsTourSeen,
    navTourSeen,
    showTour,
    showVendorDashboardUpgradeModal,
    showVendorNewExperienceModal,
    splitScreenTourSeen,
    storefrontDetails,
  ]);

  if (!showShadowboxTour) {
    return null;
  }

  return <ShadowboxTour steps={steps} onTourComplete={onTourComplete} />;
};
